import { useEffect, useState } from "react";

import { Button, IconButton, Input, Select, Switch } from "@/components/ui";

import {
  blankFor,
  clampTo,
  entriesOf,
  isSupplied,
  moved,
  pairFieldRows,
  pairValueChoices,
  scalarRefusal,
  scalarTypeOf,
  secondsOf,
  serializePairString,
  stepFor,
  type PairRow,
} from "../lib/settings";
import type {
  ScalarSettingType,
  ScalarSettingValue,
  SettingField,
  SettingValue,
} from "../types";

import { Muted } from "./Text";

const ICON_BOX = { viewBox: "0 0 16 16", width: 14, height: 14, "aria-hidden": true } as const;
const ICON_STROKE = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.4,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

/** The one glyph this file needs: a disclosure for the manifest's own `help` text. */
function HelpIcon() {
  return (
    <svg {...ICON_BOX}>
      <circle {...ICON_STROKE} cx="8" cy="8" r="6" />
      <path {...ICON_STROKE} d="M6.1 6.1a1.9 1.9 0 1 1 2.7 1.7c-.6.3-.9.8-.9 1.5" />
      <path {...ICON_STROKE} d="M7.9 11.4v.1" />
    </svg>
  );
}

interface ScalarProps {
  readonly field: SettingField;
  readonly type: ScalarSettingType;
  readonly value: ScalarSettingValue;
  readonly label: string;
  readonly onChange: (next: ScalarSettingValue) => void;
}

/** A number, committed when the person leaves it rather than on every keystroke. */
function NumberControl({ field, type, value, label, onChange }: ScalarProps) {
  const held = typeof value === "number" ? value : 0;
  const [draft, setDraft] = useState(String(held));

  useEffect(() => {
    setDraft(String(held));
  }, [held]);

  const commit = (): void => {
    const next = Number(draft);
    if (draft.trim() === "" || Number.isNaN(next)) {
      setDraft(String(held));
      return;
    }
    const bounded = clampTo(field, type === "int" ? Math.round(next) : next);
    setDraft(String(bounded));
    if (bounded !== held) onChange(bounded);
  };

  return (
    <span className="cx-stepper">
      <Input
        type="number"
        aria-label={label}
        value={draft}
        step={stepFor(field, type)}
        {...(field.min == null ? {} : { min: field.min })}
        {...(field.max == null ? {} : { max: field.max })}
        invalid={scalarRefusal(field, type, held) !== null}
        style={{ width: "10ch" }}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") commit();
        }}
      />
      {type === "duration_ms" ? <Muted small>{`ms, ${secondsOf(held)}`}</Muted> : null}
    </span>
  );
}

/** A whole number, with the two buttons a bounded int has always had here. */
function Stepper({ field, type, value, label, onChange }: ScalarProps) {
  const held = typeof value === "number" ? value : 0;
  const step = stepFor(field, type);
  return (
    <span className="cx-stepper">
      <button
        type="button"
        aria-label={`less ${label}`}
        onClick={() => {
          onChange(clampTo(field, held - step));
        }}
      >
        -
      </button>
      <span className="cx-mono">{String(held)}</span>
      <button
        type="button"
        aria-label={`more ${label}`}
        onClick={() => {
          onChange(clampTo(field, held + step));
        }}
      >
        +
      </button>
    </span>
  );
}

/** `#rrggbb` as text, because a swatch alone is a value nobody can read back. */
function ColorControl({ field, type, value, label, onChange }: ScalarProps) {
  const held = typeof value === "string" ? value : "";
  const [draft, setDraft] = useState(held);

  useEffect(() => {
    setDraft(held);
  }, [held]);

  const bad = scalarRefusal(field, type, draft) !== null;

  return (
    <span className="cx-stepper">
      <span
        className="cx-swatch"
        aria-hidden="true"
        style={{ background: bad ? "transparent" : draft }}
      />
      <Input
        mono
        aria-label={label}
        placeholder="#rrggbb"
        value={draft}
        invalid={bad}
        style={{ width: "12ch" }}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onBlur={() => {
          if (draft !== held) onChange(draft);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" && draft !== held) onChange(draft);
        }}
      />
      {(field.choices ?? []).map((choice) => (
        <button
          type="button"
          key={choice}
          aria-label={choice}
          className={choice === held ? "cx-swatch cx-swatch-on" : "cx-swatch"}
          style={{ background: choice }}
          onClick={() => {
            onChange(choice);
          }}
        />
      ))}
    </span>
  );
}

/** Text, committed on leaving it so one edit is one write. */
function TextControl({ field, type, value, label, onChange }: ScalarProps) {
  const held = typeof value === "string" ? value : "";
  const [draft, setDraft] = useState(held);

  useEffect(() => {
    setDraft(held);
  }, [held]);

  return (
    <Input
      aria-label={label}
      value={draft}
      invalid={scalarRefusal(field, type, draft) !== null}
      onChange={(event) => {
        setDraft(event.target.value);
      }}
      onBlur={() => {
        if (draft !== held) onChange(draft);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" && draft !== held) onChange(draft);
      }}
    />
  );
}

/** One value of one declared scalar type, wherever it is held. */
function ScalarControl(props: ScalarProps) {
  const { field, type, value, label, onChange } = props;

  if (type === "bool") {
    return (
      <Switch
        label={label}
        checked={value === true}
        onChange={(checked) => {
          onChange(checked);
        }}
      />
    );
  }

  if (type === "choice") {
    return (
      <span className="cx-seg">
        {(field.choices ?? []).map((choice) => (
          <button
            type="button"
            key={choice}
            className={choice === value ? "cx-seg-btn cx-seg-btn-on" : "cx-seg-btn"}
            onClick={() => {
              onChange(choice);
            }}
          >
            {choice}
          </button>
        ))}
      </span>
    );
  }

  if (type === "color") return <ColorControl {...props} />;
  if (type === "int") return <Stepper {...props} />;
  if (type === "float" || type === "duration_ms") return <NumberControl {...props} />;
  return <TextControl {...props} />;
}

/** An ordered list, each entry the scalar `of` names, added, removed and reordered by hand. */
function ListControl({
  field,
  value,
  onChange,
}: {
  readonly field: SettingField;
  readonly value: SettingValue;
  readonly onChange: (next: SettingValue) => void;
}) {
  const of = scalarTypeOf(field);
  const entries = entriesOf(value);

  return (
    <div className="cx-config" style={{ flex: 1, minWidth: "240px" }}>
      {entries.map((entry, index) => (
        <div className="cx-setting" key={`${String(index)}:${String(entry)}`}>
          <ScalarControl
            field={field}
            type={of}
            value={entry}
            label={`${field.label} ${String(index + 1)}`}
            onChange={(next) => {
              onChange(entries.map((held, at) => (at === index ? next : held)));
            }}
          />
          <span className="cx-verbs">
            <Button
              size="sm"
              variant="ghost"
              aria-label={`up ${field.label} ${String(index + 1)}`}
              disabled={index === 0}
              onClick={() => {
                onChange(moved(entries, index, -1));
              }}
            >
              Up
            </Button>
            <Button
              size="sm"
              variant="ghost"
              aria-label={`down ${field.label} ${String(index + 1)}`}
              disabled={index === entries.length - 1}
              onClick={() => {
                onChange(moved(entries, index, 1));
              }}
            >
              Down
            </Button>
            <Button
              size="sm"
              variant="ghost"
              aria-label={`remove ${field.label} ${String(index + 1)}`}
              onClick={() => {
                onChange(entries.filter((_, at) => at !== index));
              }}
            >
              Remove
            </Button>
          </span>
        </div>
      ))}
      <span className="cx-verbs">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            onChange([...entries, blankFor(field, of)]);
          }}
        >
          {`Add ${field.label}`}
        </Button>
      </span>
    </div>
  );
}

/** One row's key or value, committed on leaving it, same discipline as `TextControl`. */
function PairText({
  label,
  value,
  invalid,
  onCommit,
}: {
  readonly label: string;
  readonly value: string;
  readonly invalid?: boolean;
  readonly onCommit: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  return (
    <Input
      aria-label={label}
      value={draft}
      {...(invalid === undefined ? {} : { invalid })}
      onChange={(event) => {
        setDraft(event.target.value);
      }}
      onBlur={() => {
        if (draft !== value) onCommit(draft);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" && draft !== value) onCommit(draft);
      }}
    />
  );
}

/** A `k=v,k=v` string as one row per pair, writing the identical format back on every commit. */
function PairsControl({
  field,
  rows,
  choices,
  onChange,
}: {
  readonly field: SettingField;
  readonly rows: readonly PairRow[];
  readonly choices: readonly string[] | null;
  readonly onChange: (next: SettingValue) => void;
}) {
  const commit = (next: PairRow[]): void => {
    onChange(serializePairString(next));
  };

  return (
    <div className="cx-config" style={{ flex: 1, minWidth: "240px" }}>
      {rows.map((row, index) => (
        <div className="cx-pair-row" key={String(index)}>
          <PairText
            label={`${field.label} key ${String(index + 1)}`}
            value={row.key}
            invalid={row.key.trim() === ""}
            onCommit={(nextKey) => {
              commit(rows.map((held, at) => (at === index ? { ...held, key: nextKey } : held)));
            }}
          />
          {choices === null ? (
            <PairText
              label={`${field.label} value ${String(index + 1)}`}
              value={row.value}
              onCommit={(nextValue) => {
                commit(rows.map((held, at) => (at === index ? { ...held, value: nextValue } : held)));
              }}
            />
          ) : (
            <Select
              aria-label={`${field.label} value ${String(index + 1)}`}
              value={row.value}
              options={choices.map((choice) => ({ value: choice, label: choice }))}
              onChange={(event) => {
                const nextValue = event.target.value;
                commit(rows.map((held, at) => (at === index ? { ...held, value: nextValue } : held)));
              }}
            />
          )}
          <Button
            size="sm"
            variant="ghost"
            aria-label={`remove ${field.label} row ${String(index + 1)}`}
            onClick={() => {
              commit(rows.filter((_, at) => at !== index));
            }}
          >
            Remove
          </Button>
        </div>
      ))}
      <span className="cx-verbs">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            commit([...rows, { key: "", value: choices?.[0] ?? "" }]);
          }}
        >
          {`Add ${field.label} row`}
        </Button>
      </span>
    </div>
  );
}

/** One setting, drawn from its declared type. The activity declares it; the platform renders it. */
export function SettingControl({
  field,
  value,
  onChange,
}: {
  readonly field: SettingField;
  readonly value: SettingValue;
  readonly onChange: (next: SettingValue) => void;
}) {
  const supplied = isSupplied(field);
  const hasHelp = field.help != null && field.help !== "";
  const [helpOpen, setHelpOpen] = useState(false);

  const pairs = supplied ? null : pairFieldRows(field, value);
  const choices = pairs === null ? null : pairValueChoices(field);

  return (
    <div className="cx-setting-item">
      <div className="cx-setting-head">
        <span className="cx-setting-label">
          {field.label}
          {supplied ? <Muted small>{` supplied by ${String(field.from)}`}</Muted> : null}
        </span>
        {hasHelp ? (
          <IconButton
            label="Help"
            size="sm"
            className={helpOpen ? "cx-help-toggle cx-help-toggle-on" : "cx-help-toggle"}
            onClick={() => {
              setHelpOpen((open) => !open);
            }}
          >
            <HelpIcon />
          </IconButton>
        ) : null}
      </div>

      {supplied ? (
        <span className="cx-mono" aria-label={field.label}>
          {Array.isArray(value) ? value.join(", ") : String(value)}
        </span>
      ) : pairs !== null ? (
        <PairsControl field={field} rows={pairs} choices={choices} onChange={onChange} />
      ) : field.type === "list" ? (
        <ListControl field={field} value={value} onChange={onChange} />
      ) : (
        <ScalarControl
          field={field}
          type={field.type}
          value={Array.isArray(value) ? "" : value}
          label={field.label}
          onChange={onChange}
        />
      )}

      {hasHelp && helpOpen ? <Muted small>{field.help}</Muted> : null}
    </div>
  );
}
